import { useState } from "react";
import { toast } from "sonner";
import { api, OBJECTIVES, BUDGETS } from "@/lib/spott";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const empty = {
  contact_name: "", company_name: "", contact_email: "", contact_phone: "",
  objective: "", budget_range: "", message: "",
};

export const ProposalDialog = ({ open, onOpenChange }) => {
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/proposal-requests", f);
      setDone(true);
      setF(empty);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos enviar tu solicitud, inténtalo de nuevo");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setDone(false); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="proposal-dialog">
        <DialogHeader>
          <DialogTitle className="text-spott-blue">
            {done ? "¡Solicitud recibida!" : "Quiero mi propuesta gratis"}
          </DialogTitle>
        </DialogHeader>

        {done ? (
          <div className="space-y-4 py-2" data-testid="proposal-success">
            <p className="text-sm text-gray-600">
              Un estratega de Spott revisará tu objetivo y presupuesto y te enviará una propuesta 360°
              inicial sin costo en las próximas 48 horas hábiles.
            </p>
            <button onClick={() => onOpenChange(false)} data-testid="proposal-close-btn"
              className="w-full rounded-full bg-spott-blue py-3 text-sm font-bold text-white">
              Listo
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-sm text-gray-500">
              Sin compromiso. Déjanos tus datos, objetivo y presupuesto estimado.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-spott-blue">Nombre *</Label>
                <Input required value={f.contact_name} onChange={set("contact_name")}
                  data-testid="proposal-name" placeholder="Tu nombre" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-spott-blue">Empresa / marca *</Label>
                <Input required value={f.company_name} onChange={set("company_name")}
                  data-testid="proposal-company" placeholder="Ej: Lácteos Andinos" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-spott-blue">Correo *</Label>
                <Input required type="email" value={f.contact_email} onChange={set("contact_email")}
                  data-testid="proposal-email" placeholder="tu@empresa.com" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-spott-blue">Teléfono</Label>
                <Input value={f.contact_phone} onChange={set("contact_phone")}
                  data-testid="proposal-phone" placeholder="09xxxxxxxx" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-spott-blue">Objetivo de campaña *</Label>
              <div className="flex flex-wrap gap-2" data-testid="proposal-objectives">
                {OBJECTIVES.map((o) => (
                  <button key={o.id} type="button" onClick={() => setF({ ...f, objective: o.label })}
                    data-testid={`proposal-objective-${o.id}`}
                    className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                      f.objective === o.label
                        ? "border-spott-blue bg-spott-blue text-white"
                        : "border-spott-mid bg-white text-spott-blue hover:border-spott-yellow"
                    }`}>{o.label}</button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-spott-blue">Rango de inversión *</Label>
              <div className="flex flex-wrap gap-2" data-testid="proposal-budgets">
                {BUDGETS.map((b) => {
                  const label = b.label || b;
                  return (
                    <button key={label} type="button" onClick={() => setF({ ...f, budget_range: label })}
                      data-testid={`proposal-budget-${label}`}
                      className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                        f.budget_range === label
                          ? "border-spott-blue bg-spott-blue text-white"
                          : "border-spott-mid bg-white text-spott-blue hover:border-spott-yellow"
                      }`}>{label}</button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-spott-blue">Cuéntanos más (opcional)</Label>
              <Textarea value={f.message} onChange={set("message")} rows={3} data-testid="proposal-message"
                placeholder="Producto, temporada, ciudades de interés…" />
            </div>

            <button type="submit" data-testid="proposal-submit"
              disabled={busy || !f.objective || !f.budget_range}
              className="w-full rounded-full bg-spott-yellow py-3.5 text-sm font-bold text-spott-blue transition-transform hover:scale-[1.01] disabled:opacity-50">
              {busy ? "Enviando…" : "Enviar solicitud"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
